import { useCallback, useEffect, useMemo, useState } from "react";import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, FileDown } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import DataTable from "@/components/DataTable";

export default function Billing() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [i, p] = await Promise.all([api.get("/invoices"), api.get("/patients")]);
      setItems(i.data); setPatients(p.data);
    } catch (err) {
      console.error("Failed to load invoices:", err);
      toast.error("Failed to load");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const bulkDelete = async (ids) => {
    try {
      const { data } = await api.post("/invoices/bulk-delete", { ids });
      toast.success(`Deleted ${data.deleted} invoice(s)`);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Bulk delete failed"); }
  };

  const openPdf = useCallback(async (id) => {
    try {
      const r = await api.get(`/invoices/${id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Failed to load invoice PDF"); }
  }, []);

  const renderRowExtras = useCallback(
    (inv) => (
      <Button variant="outline" size="sm" className="rounded-full text-xs h-8" onClick={() => openPdf(inv.id)} data-testid={`inv-pdf-${inv.id}`}>
        <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
      </Button>
    ),
    [openPdf]
  );

  const columns = useMemo(() => ([
    {
      key: "created_at", label: "Date", sortable: true,
      accessor: (i) => i.created_at,
      render: (i) => <span className="text-[color:var(--muted)]">{new Date(i.created_at).toLocaleDateString()}</span>,
    },
    { key: "patient_name", label: "Patient", sortable: true, accessor: (i) => i.patient_name, render: (i) => <span className="font-medium text-[color:var(--ink)]">{i.patient_name}</span> },
    { key: "total", label: "Total", sortable: true, align: "right", accessor: (i) => i.total || 0, render: (i) => `₹${(i.total || 0).toLocaleString()}` },
    { key: "paid_amount", label: "Paid", sortable: true, align: "right", accessor: (i) => i.paid_amount || 0, render: (i) => <span className="text-[color:var(--muted)]">₹{(i.paid_amount || 0).toLocaleString()}</span> },
    { key: "balance", label: "Balance", sortable: true, align: "right", accessor: (i) => i.balance || 0, render: (i) => <span className="text-[color:var(--muted)]">₹{(i.balance || 0).toLocaleString()}</span> },
    { key: "payment_mode", label: "Mode", sortable: true, defaultHidden: true, accessor: (i) => i.payment_mode || "", render: (i) => <span className="capitalize">{i.payment_mode || "—"}</span> },
    {
      key: "status", label: "Status", sortable: true, align: "right",
      accessor: (i) => i.status,
      render: (i) => (
        <span className={`text-[10px] tracking-wider uppercase px-2.5 py-1 rounded-full ${i.status === "paid" ? "bg-green-100 text-green-700" : i.status === "partial" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{i.status}</span>
      ),
    },
  ]), []);

  return (
    <div data-testid="billing-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-7">
        <div>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-[color:var(--muted)]">Finance</p>
          <h1 className="font-heading text-3xl md:text-4xl text-[color:var(--ink)] font-medium mt-1">Billing & invoices</h1>
        </div>
        <InvoiceDialog open={createOpen} setOpen={setCreateOpen} patients={patients} onSaved={load} />
      </div>

      <DataTable
        storageKey="invoices"
        rows={items}
        columns={columns}
        isAdmin={isAdmin}
        onEdit={isAdmin ? setEditing : undefined}
        onDeleteMany={isAdmin ? bulkDelete : undefined}
        emptyMessage="No invoices yet."
        minWidth={780}
        extraRowActions={renderRowExtras}
      />

      {editing && (
        <InvoiceDialog
          open={!!editing}
          setOpen={(v) => { if (!v) setEditing(null); }}
          patients={patients}
          existing={editing}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function InvoiceDialog({ open, setOpen, patients, onSaved, existing }) {
  const isEdit = !!existing;
  const [patient_id, setPid] = useState("");
  const [items, setItems] = useState([{ description: "Consultation", qty: 1, price: 1500 }]);
  const [tax, setTax] = useState(0);
  const [paid, setPaid] = useState(0);
  const [mode, setMode] = useState("upi");
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (existing) {
      setPid(existing.patient_id || "");
      setItems(existing.items?.length ? existing.items.map((it) => ({ description: it.description || "", qty: it.qty ?? 1, price: it.price ?? 0 })) : [{ description: "", qty: 1, price: 0 }]);
      setTax(existing.tax_percent || 0);
      setPaid(existing.paid_amount || 0);
      setMode(existing.payment_mode || "upi");
      setStatus(existing.status || "pending");
    } else {
      setPid("");
      setItems([{ description: "Consultation", qty: 1, price: 1500 }]);
      setTax(0); setPaid(0); setMode("upi"); setStatus("pending");
    }
  }, [existing, open]);

  const total = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.price) || 0), 0) * (1 + tax / 100);

  const save = async () => {
    setLoading(true);
    try {
      const payload = { patient_id, items, tax_percent: Number(tax), paid_amount: Number(paid), payment_mode: mode, status };
      if (isEdit) {
        await api.put(`/invoices/${existing.id}`, payload);
        toast.success("Invoice updated");
      } else {
        await api.post("/invoices", payload);
        toast.success("Invoice created");
      }
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isEdit && (
        <DialogTrigger asChild>
          <Button className="rounded-full bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="new-invoice-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New invoice
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="invoice-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit invoice" : "New invoice"}</DialogTitle>
          <DialogDescription className="text-[color:var(--muted)]">{isEdit ? "Update line items, tax, payment status or amount paid." : "Add line items, tax and capture payment."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          <div>
            <label className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)]">Patient *</label>
            <Select value={patient_id} onValueChange={setPid}>
              <SelectTrigger className="mt-1.5" data-testid="invoice-patient-select"><SelectValue placeholder="Select patient" /></SelectTrigger>
              <SelectContent>
                {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <p className="text-xs font-semibold tracking-wider uppercase text-[color:var(--muted)] mb-2">Line items</p>
            <div className="space-y-2">
              {items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2" data-testid={`invoice-item-${i}`}>
                  <Input className="col-span-6" value={it.description} onChange={(e) => { const c = [...items]; c[i].description = e.target.value; setItems(c); }} placeholder="Description" />
                  <Input className="col-span-2" type="number" value={it.qty} onChange={(e) => { const c = [...items]; c[i].qty = e.target.value; setItems(c); }} placeholder="Qty" />
                  <Input className="col-span-3" type="number" value={it.price} onChange={(e) => { const c = [...items]; c[i].price = e.target.value; setItems(c); }} placeholder="Price" />
                  <button type="button" onClick={() => setItems(items.filter((_, j) => j !== i))} className="col-span-1 text-[color:var(--muted)] hover:text-red-500" data-testid={`remove-item-${i}`}><Trash2 className="w-4 h-4 mx-auto" /></button>
                </div>
              ))}
            </div>
            <Button variant="outline" size="sm" className="rounded-full mt-3" onClick={() => setItems([...items, { description: "", qty: 1, price: 0 }])} data-testid="add-line-item">+ Add line</Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[color:var(--muted)]">Tax %</label>
              <Input type="number" value={tax} onChange={(e) => setTax(e.target.value)} className="mt-1.5" data-testid="invoice-tax" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[color:var(--muted)]">Paid</label>
              <Input type="number" value={paid} onChange={(e) => setPaid(e.target.value)} className="mt-1.5" data-testid="invoice-paid" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[color:var(--muted)]">Mode</label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger className="mt-1.5" data-testid="invoice-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="upi">UPI</SelectItem>
                  <SelectItem value="card">Card</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-[color:var(--muted)]">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="mt-1.5" data-testid="invoice-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="partial">Partial</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="text-right text-lg font-heading text-[color:var(--ink)]">Total: ₹{total.toFixed(2)}</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} data-testid="invoice-cancel">Cancel</Button>
          <Button onClick={save} disabled={loading || !patient_id} className="bg-[color:var(--primary)] hover:bg-[color:var(--primary-hover)] text-white" data-testid="invoice-save">{loading ? "Saving…" : isEdit ? "Save changes" : "Save invoice"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
